import React, { useState, useEffect, RefObject } from 'react';
import './TimeMarker.css';

interface TimeMarkerProps {
  timelineContainerRef: RefObject<HTMLDivElement>;
  timeAxisRef: RefObject<HTMLDivElement>;
  timeRange: {
    start: Date;
    end: Date;
  };
}

const TimeMarker: React.FC<TimeMarkerProps> = ({
  timelineContainerRef,
  timeAxisRef,
  timeRange
}) => {
  // State to track mouse position
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  
  // Track mouse movement relative to time axis
  useEffect(() => {
    const timeAxisContainer = timeAxisRef.current;
    const scrollContainer = timeAxisRef.current?.parentElement; 

    if (!scrollContainer || !timeAxisContainer) return;
    
    // Find the first child of scrollContainer with class time-bar
    const timeBar = scrollContainer?.querySelector('.time-bars');

    const handleMouseMove = (event: MouseEvent) => {
      const containerRect = timeAxisContainer.getBoundingClientRect();
      const relativeX = event.clientX - containerRect.left;
      if (timeBar && (
        event.clientX < timeBar.getBoundingClientRect()?.left ||
        event.clientX > timeBar.getBoundingClientRect().right
      ))
        setCursorPosition(null);
      else
        setCursorPosition(relativeX);
    };
    
    const handleMouseLeave = () => {
      setCursorPosition(null);
    };
    
    scrollContainer.addEventListener('mousemove', handleMouseMove);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [timeAxisRef]);

  // If no cursor position, don't render anything
  if (cursorPosition === null) return null;

  // Calculate marker position
  const markerPosition = (timeAxisRef.current?.offsetLeft || 0) + cursorPosition;
  
  // Calculate the time at the marker position
  let markerTime = '';
  if (timelineContainerRef.current && timeAxisRef.current) {
    // Get the timeline dimensions
    const timeAxisWidth = timeAxisRef.current.offsetWidth;
    
    // Calculate start and end hours
    const startHour = timeRange.start.getHours();
    const endHour = timeRange.end.getHours();
    const totalMinutes = (endHour - startHour) * 60;
    
    // Calculate what percentage of the time axis width this position represents
    const positionRatio = Math.max(0, Math.min(1, cursorPosition / timeAxisWidth));
    
    // Convert position ratio to minutes from start time
    const minutesFromStart = positionRatio * totalMinutes;
    
    // Calculate the actual time
    const markerDate = new Date(timeRange.start);
    markerDate.setMinutes(markerDate.getMinutes() + minutesFromStart);
    
    // Format the time
    markerTime = markerDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div 
      className="time-cursor-line" 
      style={{ left: `${markerPosition}px` }}
    >
      <div className="time-cursor-label">{markerTime}</div>
    </div>
  );
};

export default TimeMarker;
